<template>
  <BaseCard @click.stop>
    <BaseCardHeader>
      <button
        type="button"
        class="relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md bg-white focus:outline-none border-gray-300 text-gray-700 hover:bg-gray-50 focus:border-blue-600"
        @click="$emit('close')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 mr-3 -ml-1"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        {{ $t('filterManager.filterOption.back') }}
      </button>
    </BaseCardHeader>
    <BaseCardBody clear-padding>
      <div
        v-for="filterItem in filters"
        :key="filterItem.name"
        class="px-6 space-y-2 py-4"
        :class="[selectedFilterName === filterItem.name ? 'bg-blue-50' : 'bg-white']"
      >
        <div class="flex items-center">
          <input
            type="radio"
            :name="filterItem.name"
            :id="filterItem.name"
            :checked="selectedFilterName === filterItem.name"
            @change="setSelectedFilter(filterItem)"
          />
          <label
            :for="filterItem.name"
            class="ml-3 block text-sm font-medium text-gray-700 select-none"
          >
            {{
              $te(`filterManager.filters.${name}.${filterItem.name}.label`)
                ? $t(`filterManager.filters.${name}.${filterItem.name}.label`)
                : filterItem.label
            }}
          </label>
        </div>
        <template v-if="selectedFilter && selectedFilterName === filterItem.name">
          <BaseChipsBasic
            v-if="['chips', 'chips:multiple'].indexOf(selectedFilter.input) >= 0"
            v-model="selectedFilter.value"
            :options="option.data"
            :multiple="selectedFilter.input === 'chips:multiple'"
          />

          <BaseFormInput
            v-else-if="['date', 'text', 'number'].indexOf(selectedFilter.input) >= 0"
            :type="selectedFilter.input"
            v-model="selectedFilter.value"
            class="w-auto"
          />

          <div
            v-else-if="selectedFilter.input === 'date-range'"
            class="flex items-center space-x-3"
          >
            <BaseFormInput type="date" v-model="selectedFilter.value[0]" class="w-auto" />
            <span>-</span>
            <BaseFormInput type="date" v-model="selectedFilter.value[1]" class="w-auto" />
          </div>

          <ul
            v-else-if="
              ['grouped-chips', 'grouped-chips:multiple'].indexOf(selectedFilter.input) >= 0
            "
            class="space-y-8 pl-6"
          >
            <li v-for="group in option.data" :key="group.id" class="space-y-2">
              <h2 class="text-sm font-medium text-gray-500">
                {{ group.label }}
              </h2>
              <div class="-mx-1">
                <BaseChipsBasic
                  v-model="selectedFilter.value"
                  :options="group.options"
                  :multiple="selectedFilter.input === 'grouped-chips:multiple'"
                />
              </div>
            </li>
            <li v-if="paginated" class="flex items-center justify-center space-x-4">
              <BaseButton primary outline small :disabled="!hasPrevPage" @click="loadPrevPage">
                {{ $t('pagination.previous') }}
              </BaseButton>
              <BaseButton primary outline small :disabled="!hasNextPage" @click="loadNextPage">
                {{ $t('pagination.next') }}
              </BaseButton>
            </li>
          </ul>
        </template>
      </div>
    </BaseCardBody>
    <BaseCardBody>
      <div class="flex-1">
        <BaseButton
          type="button"
          primary
          :disabled="!selectedFilter"
          class="w-full"
          @click="applyFilterOption"
        >
          {{ $t('filterManager.filterOption.applyFilter') }}
        </BaseButton>
      </div>
    </BaseCardBody>
  </BaseCard>
</template>

<script>
export default {
  props: {
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    filters: {
      type: Array,
      default: () => [],
    },
    fetchOptions: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      selectedFilter: null,
      option: {
        data: [],
        meta: null,
      },
    };
  },
  computed: {
    selectedFilterName() {
      return this.selectedFilter?.name || null;
    },
    paginated() {
      return this.option.meta !== null && this.option.meta.last_page > 1;
    },
    hasPrevPage() {
      if (!this.paginated) {
        return false;
      }

      return this.option.meta.current_page > 1;
    },
    hasNextPage() {
      if (!this.paginated) {
        return false;
      }

      return this.option.meta.current_page < this.option.meta.last_page;
    },
  },
  created() {
    if (typeof this.fetchOptions === 'function') {
      this.loadOptions();
    }
  },
  methods: {
    async loadOptions(queryParams = {}) {
      try {
        const response = await this.fetchOptions(queryParams);

        this.option.data = response.data;
        this.option.meta = response?.meta || null;
      } catch (error) {
        this.option.data = [];
        this.option.meta = null;
      }
    },
    loadPrevPage() {
      this.loadOptions({ page: { number: this.option.meta.current_page - 1 } });
    },
    loadNextPage() {
      this.loadOptions({ page: { number: this.option.meta.current_page + 1 } });
    },
    setSelectedFilter(filterItem) {
      this.selectedFilter = { ...filterItem };
    },
    applyFilterOption() {
      this.$emit('apply', { key: this.selectedFilter.name, value: this.selectedFilter.value });
    },
  },
};
</script>
